import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { PaymentTypeEnum } from '../../../common/enums.js';
import { initiateCardPaymentForDeviceOrder, submitProductOrder } from '../../../common/fetch.js';
import { saveFormDataToSession } from '../utils/cardPaymentUtilsCoCo.js';
import { requireValue } from '../utils/placeOrderUtils.js';
import type { PlaceOrderFormDataCoCo } from '../placeOrderTypesCoCo.js';

interface UsePlaceOrderCoCoProps {
  orderId: string;
  getFormValues: () => PlaceOrderFormDataCoCo;
  onError?: (errorMessage: string) => void;
  isEmployeeOrder?: boolean;
}

interface UsePlaceOrderCoCoReturn {
  isSubmittingOrder: boolean;
  submitOrder: (billingAccountId: string) => Promise<void>;
}

export const usePlaceOrderCoCo = ({
  orderId,
  getFormValues,
  onError,
  isEmployeeOrder = false,
}: UsePlaceOrderCoCoProps): UsePlaceOrderCoCoReturn => {
  const navigate = useNavigate();
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const submissionInProgress = useRef(false);

  const submitOrder = async (billingAccountId: string): Promise<void> => {
    if (submissionInProgress.current) {
      return;
    }

    submissionInProgress.current = true;
    setIsSubmittingOrder(true);

    try {
      // 1. Validate required fields
      const productOrderId = requireValue(orderId || undefined, 'Order id is missing');
      const formData = getFormValues();
      const paymentType = requireValue(formData.payment?.paymentType, 'Payment type is required');
      requireValue(formData.products?.placeOrderItemUnits?.[0], 'Order must contain at least one product');
      requireValue(
        paymentType === PaymentTypeEnum.CARD || paymentType === PaymentTypeEnum.BILLING_ACCOUNT
          ? paymentType
          : undefined,
        'Payment type is invalid'
      );

      // 2. Card payment: the order is stored as part of the payment initiation, so no separate submit call
      if (paymentType === PaymentTypeEnum.CARD) {
        const cardPayment = await initiateCardPaymentForDeviceOrder(productOrderId);
        const transactionId = requireValue(cardPayment?.transactionId, 'Card payment initiation failed');
        const forwardUrl = requireValue(cardPayment?.forwardUrl, 'Card payment initiation failed');

        // Save form data to session before redirect (for thank you page)
        saveFormDataToSession({ formData, transactionId, orderId: productOrderId, timestamp: Date.now() });

        // Redirect to Nets payment page (full page navigation)
        window.location.href = forwardUrl;
        return;
      }

      // 3. Billing account: submit the order and go straight to the thank you page
      const billingAccount = requireValue(billingAccountId || undefined, 'Billing account is required');
      const submittedOrder = await submitProductOrder(productOrderId, billingAccount);

      saveFormDataToSession({
        formData,
        transactionId: '',
        orderId: submittedOrder?.id ?? productOrderId,
        timestamp: Date.now(),
      });

      navigate(isEmployeeOrder ? paths.EMPLOYEE_DEVICE_CHECKOUT_THANK_YOU : paths.PLACE_ORDER_THANK_YOU);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      submissionInProgress.current = false;
      setIsSubmittingOrder(false);
      onError?.(errorMessage);
    }
  };

  return { isSubmittingOrder, submitOrder };
};
